import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Info,
  Megaphone,
  MousePointerClick,
  ShoppingBag,
  Target,
  type LucideIcon,
} from "lucide-react";
import {
  financeService,
  type FinanceMetaAdPerformance,
  type FinanceMetaCampaignPerformance,
} from "../../../services/financeService";
import type { Period } from "../../funnel-detail/types";
import type { CompanyData, FunnelMonthlyRow } from "./types";
import { formatCurrency, formatCurrencyCompact, formatNumber } from "./utils";

type Props = {
  data: CompanyData;
  period: Period;
};

type MarketingMetrics = {
  reach: number;
  spend: number;
  impressions: number;
  clicks: number;
  checkoutViews: number;
  purchases: number;
  revenue: number;
  ctr: number;
  cpa: number;
  cpm: number;
  frequency: number;
  clickToCheckout: number;
  roas: number;
};

type FunnelScoreRow = {
  funnel: FunnelMonthlyRow;
  metrics: MarketingMetrics;
  score: number;
  reason: string;
};

const SCORE_CHECKOUT_BENCHMARK = 8;
const SCORE_CTR_BENCHMARK = 1.5;
const SCORE_ROAS_TARGET = 2;

const formatPercent = (value: number) =>
  `${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 1 }).format(Number.isFinite(value) ? value : 0)}%`;

const formatMultiplier = (value: number) =>
  `${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 1 }).format(Number.isFinite(value) ? value : 0)}x`;

export function CompanyMarketingFunnel({ data, period }: Props) {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<FinanceMetaCampaignPerformance[]>([]);
  const [ads, setAds] = useState<FinanceMetaAdPerformance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    Promise.all([
      financeService.getFinanceCampaignPerformance(period),
      financeService.getFinanceAdsPerformance(period),
    ])
      .then(([campaignRows, adRows]) => {
        if (!mounted) return;
        setCampaigns(campaignRows);
        setAds(adRows);
      })
      .catch(() => {
        if (!mounted) return;
        setCampaigns([]);
        setAds([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [period]);

  const globalMetrics = useMemo(() => {
    return buildMetrics({
      campaigns,
      ads,
      checkoutViews: Number(data.stages.checkoutViews ?? 0),
      revenue: data.collectedRevenue ?? data.totalRevenue,
      purchases: data.stages.ordersCreated,
      fallbackSpend: data.totalAdInvestment,
    });
  }, [ads, campaigns, data.collectedRevenue, data.stages.checkoutViews, data.stages.ordersCreated, data.totalAdInvestment, data.totalRevenue]);

  const rankedFunnels = useMemo(() => {
    const adRowsByCampaign = new Map<string, FinanceMetaAdPerformance[]>();
    ads.forEach((ad) => {
      const rows = adRowsByCampaign.get(ad.campaign_id) ?? [];
      rows.push(ad);
      adRowsByCampaign.set(ad.campaign_id, rows);
    });

    return data.funnels
      .map((funnel) => {
        const matchedCampaigns = campaigns.filter((campaign) =>
          campaign.matched_funnel_ids?.includes(funnel.funnelId)
        );
        const matchedAds = matchedCampaigns.flatMap((campaign) => adRowsByCampaign.get(campaign.id) ?? []);
        const metrics = buildMetrics({
          campaigns: matchedCampaigns,
          ads: matchedAds,
          checkoutViews: Number(funnel.checkoutViews ?? 0),
          revenue: funnel.revenue,
          fallbackSpend: funnel.adInvestment,
        });
        const score = calculateScore(metrics, funnel, data.avgCpa);
        return {
          funnel,
          metrics,
          score,
          reason: buildScoreReason(metrics, score),
        };
      })
      .sort((a, b) => b.score - a.score);
  }, [ads, campaigns, data.avgCpa, data.funnels]);

  const ranking = useMemo(() => splitRanking(rankedFunnels), [rankedFunnels]);

  if (!loading && campaigns.length === 0 && data.funnels.length === 0) {
    return (
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <h2 className="text-base font-semibold text-gray-900">Funnel consolidado de marketing</h2>
        <p className="mt-1 text-sm text-gray-500">
          Cuando tengas embudos y campañas conectadas, aquí verás dónde se gana o se pierde la atención.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Funnel consolidado de marketing</h2>
          <p className="mt-1 text-sm text-gray-500">
            Una lectura global para detectar si el negocio falla en alcance, clic, checkout o compra.
          </p>
        </div>
        <span className="w-fit rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-bold text-[#2b5ce6]">
          {formatNumber(data.funnels.length)} {data.funnels.length === 1 ? "embudo" : "embudos"}
        </span>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2 lg:items-start">
        <MarketingFunnel metrics={globalMetrics} />
        <StageInsights metrics={globalMetrics} />
      </div>

      <div className="mt-5 border-t border-gray-100 pt-5">
        <RankingPanel
          ranking={ranking}
          totalFunnels={data.funnels.length}
          onOpen={(funnelId) => navigate(`/finance/funnels/${funnelId}?tab=marketing`)}
        />
      </div>
    </section>
  );
}

function MarketingFunnel({ metrics }: { metrics: MarketingMetrics }) {
  const stages = [
    {
      id: "impressions",
      title: "Impresiones",
      label: "Impactos totales",
      value: formatNumber(metrics.impressions),
      support: `CPM ${formatCurrency(metrics.cpm)}`,
      width: 100,
      color: "#2563eb",
    },
    {
      id: "reach",
      title: "Alcance",
      label: "Personas únicas",
      value: formatNumber(metrics.reach),
      support: `${formatMultiplier(metrics.frequency)} vistas/persona`,
      width: 90,
      color: "#2f6df6",
    },
    {
      id: "clicks",
      title: "Clics",
      label: "Entrada al embudo",
      value: formatNumber(metrics.clicks),
      support: `CTR ${formatPercent(metrics.ctr)}`,
      width: 78,
      color: "#20a697",
    },
    {
      id: "checkout",
      title: "Checkouts",
      label: "Checkouts",
      value: formatNumber(metrics.checkoutViews),
      support: `${formatPercent(metrics.clickToCheckout)} a checkout`,
      width: 66,
      color: "#25324d",
    },
    {
      id: "purchase",
      title: "Compras",
      label: "Compras registradas",
      value: formatNumber(metrics.purchases),
      support: metrics.purchases > 0 && metrics.roas === 0 ? "Recaudo pendiente" : `ROAS ${formatMultiplier(metrics.roas)}`,
      width: 56,
      color: "#111827",
    },
  ];

  return (
    <div className="w-full rounded-lg bg-slate-50/60 px-3 py-4 sm:px-5">
      <div className="flex flex-col gap-3">
        {stages.map((stage) => (
          <div key={stage.id} className="mx-auto" style={{ width: `${stage.width}%`, minWidth: "min(100%, 280px)" }}>
            <div className="relative h-[72px] text-white">
              <div
                className="absolute inset-0 shadow-sm"
                style={{
                  backgroundColor: stage.color,
                  clipPath: "polygon(0 0, 100% 0, 92% 100%, 8% 100%)",
                }}
              />
              <div
                className="relative flex h-[72px] items-center justify-between gap-3 py-3"
                style={{ paddingLeft: "9%", paddingRight: "9%" }}
              >
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-normal text-white/70">{stage.title}</p>
                  <p className="mt-1 text-sm font-semibold leading-tight">{stage.label}</p>
                </div>
                <div className="w-[148px] shrink-0 text-right">
                  <p className="whitespace-nowrap text-xl font-bold tracking-normal sm:text-2xl">{stage.value}</p>
                  <p className="mt-0.5 whitespace-nowrap text-[11px] font-semibold leading-tight text-white/70">
                    {stage.support}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StageInsights({ metrics }: { metrics: MarketingMetrics }) {
  const rows = [
    {
      icon: BarChart3,
      title: "Impresiones",
      metric: `${formatNumber(metrics.impressions)} impresiones`,
      text:
        metrics.impressions > 0
          ? `CPM ${formatCurrency(metrics.cpm)} por cada mil veces que el anuncio se mostró.`
          : "Sin impresiones para evaluar entrega.",
      tone: metrics.impressions > 0 ? "good" : "warning",
    },
    {
      icon: Megaphone,
      title: "Alcance",
      metric: `${formatNumber(metrics.reach)} personas`,
      text:
        metrics.reach > 0
          ? `${formatMultiplier(metrics.frequency)} vistas por persona; mide repetición sobre audiencia única.`
          : "Sin alcance registrado en el periodo.",
      tone: metrics.reach > 0 ? "good" : "warning",
    },
    {
      icon: MousePointerClick,
      title: "Clics",
      metric: `${formatNumber(metrics.clicks)} clics`,
      text:
        metrics.clicks > 0
          ? `CTR ${formatPercent(metrics.ctr)}: mide si la promesa genera respuesta.`
          : "Hay que activar interés antes de leer checkout.",
      tone: metrics.ctr >= SCORE_CTR_BENCHMARK ? "good" : "warning",
    },
    {
      icon: Target,
      title: "Checkouts",
      metric: `${formatNumber(metrics.checkoutViews)} checkouts`,
      text:
        metrics.checkoutViews > 0
          ? `${formatPercent(metrics.clickToCheckout)} de los clics llegan al checkout.`
          : "Si hay clics y no hay checkouts, revisa match, página u oferta.",
      tone: metrics.checkoutViews > 0 ? "good" : "warning",
    },
    {
      icon: ShoppingBag,
      title: "Compras",
      metric: `${formatNumber(metrics.purchases)} compras`,
      text:
        metrics.purchases > 0
          ? metrics.roas > 0
            ? `CPA ${formatCurrency(metrics.cpa)} y ROAS ${formatMultiplier(metrics.roas)}.`
            : `CPA ${formatCurrency(metrics.cpa)}; falta recaudo registrado para calcular retorno.`
          : "Todavía no hay compras para confirmar retorno.",
      tone: metrics.purchases > 0 ? "good" : "neutral",
    },
  ] as const;

  return (
    <aside className="grid gap-3">
      {rows.map((row) => (
        <InsightRow key={row.title} {...row} />
      ))}
    </aside>
  );
}

function InsightRow({
  icon: Icon,
  title,
  metric,
  text,
  tone,
}: {
  icon: LucideIcon;
  title: string;
  metric: string;
  text: string;
  tone: "good" | "warning" | "neutral";
}) {
  const toneClass =
    tone === "good"
      ? "border-l-emerald-400 text-emerald-900"
      : tone === "warning"
      ? "border-l-amber-400 text-amber-900"
      : "border-l-slate-300 text-slate-800";

  return (
    <div className={`flex h-[72px] items-center rounded-md border border-l-4 border-gray-100 bg-white px-3 py-2 ${toneClass}`}>
      <div className="flex w-full items-center gap-2.5">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-slate-50 text-current">
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
            <p className="text-sm font-bold">{title}</p>
            <p className="text-xs font-semibold opacity-70">{metric}</p>
          </div>
          <p className="mt-1 text-xs font-medium leading-5 opacity-80">{text}</p>
        </div>
      </div>
    </div>
  );
}

function RankingPanel({
  ranking,
  totalFunnels,
  onOpen,
}: {
  ranking: ReturnType<typeof splitRanking>;
  totalFunnels: number;
  onOpen: (funnelId: string) => void;
}) {
  if (totalFunnels === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-5 text-sm text-gray-500">
        Crea o activa embudos para comparar dónde conviene invertir primero.
      </div>
    );
  }

  if (totalFunnels === 1 && ranking.best[0]) {
    return (
      <div>
        <SectionHeading title="Tu embudo activo" description="Aún no hay suficientes embudos para comparar ganadores y alertas." />
        <div className="mt-3 max-w-xl">
          <FunnelScoreCard row={ranking.best[0]} onOpen={onOpen} />
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div>
        <SectionHeading title="Mejores embudos" description="Los que mejor convierten la inversión en resultado." />
        <div className="mt-3 grid gap-3">
          {ranking.best.map((row) => (
            <FunnelScoreCard key={row.funnel.funnelId} row={row} onOpen={onOpen} />
          ))}
        </div>
      </div>
      <div>
        <SectionHeading title="Necesitan atención" description="Los que muestran más fricción o menor retorno." />
        <div className="mt-3 grid gap-3">
          {ranking.attention.map((row) => (
            <FunnelScoreCard key={row.funnel.funnelId} row={row} onOpen={onOpen} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SectionHeading({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-gray-900">{title}</h3>
      <p className="mt-0.5 text-xs text-gray-500">{description}</p>
    </div>
  );
}

function FunnelScoreCard({ row, onOpen }: { row: FunnelScoreRow; onOpen: (funnelId: string) => void }) {
  const tooltip = [
    `Salud ${Math.round(row.score)}/100`,
    `ROAS ${formatMultiplier(row.metrics.roas)}`,
    `CPA ${row.metrics.cpa > 0 ? formatCurrency(row.metrics.cpa) : "$ 0"}`,
    `CTR ${formatPercent(row.metrics.ctr)}`,
    `Checkout ${formatPercent(row.metrics.clickToCheckout)}`,
  ].join(" · ");

  return (
    <button
      type="button"
      onClick={() => onOpen(row.funnel.funnelId)}
      className="group flex w-full min-w-0 items-center justify-between gap-3 overflow-hidden rounded-lg border border-gray-100 bg-white px-4 py-3 text-left shadow-sm transition hover:border-blue-100 hover:bg-blue-50/30"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate text-sm font-bold text-gray-900">{row.funnel.funnelName}</p>
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-[11px] font-bold text-slate-600">
            Salud {Math.round(row.score)}
            <span aria-label={tooltip} title={tooltip}>
              <Info className="h-3 w-3" />
            </span>
          </span>
        </div>
        <p className="mt-1 truncate text-xs text-gray-400">{row.funnel.productName}</p>
        <p className="mt-1 line-clamp-2 break-words text-xs font-medium leading-5 text-gray-600">{row.reason}</p>
      </div>
      <div className="shrink-0 text-right">
        <p className={row.funnel.profit < 0 ? "text-sm font-bold text-red-700" : "text-sm font-bold text-emerald-700"}>
          {formatCurrencyCompact(row.funnel.profit)}
        </p>
        <p className="mt-1 text-[11px] font-semibold text-gray-400">utilidad</p>
        <ArrowRight className="ml-auto mt-2 h-4 w-4 text-gray-300 transition group-hover:text-[#2b5ce6]" />
      </div>
    </button>
  );
}

function buildMetrics({
  campaigns,
  ads,
  checkoutViews,
  revenue,
  purchases,
  fallbackSpend,
}: {
  campaigns: FinanceMetaCampaignPerformance[];
  ads: FinanceMetaAdPerformance[];
  checkoutViews: number;
  revenue: number;
  purchases?: number;
  fallbackSpend: number;
}): MarketingMetrics {
  const campaignSpend = campaigns.reduce((sum, row) => sum + Number(row.spend ?? 0), 0);
  const spend = campaignSpend > 0 ? campaignSpend : Number(fallbackSpend ?? 0);
  const impressions = campaigns.reduce((sum, row) => sum + Number(row.impressions ?? 0), 0);
  const clicks = campaigns.reduce((sum, row) => sum + Number(row.clicks ?? 0), 0);
  const reach = ads.reduce((sum, row) => sum + Number(row.reach ?? 0), 0);
  const metaPurchases = campaigns.reduce((sum, row) => sum + Number(row.purchases ?? 0), 0);
  const purchaseValue = campaigns.reduce((sum, row) => sum + Number(row.purchase_value ?? 0), 0);
  const resolvedPurchases = Number(purchases ?? 0) > 0 ? Number(purchases) : metaPurchases;
  const resolvedRevenue = Number(revenue ?? 0) > 0 ? Number(revenue) : purchaseValue;

  return {
    reach,
    spend,
    impressions,
    clicks,
    checkoutViews,
    purchases: resolvedPurchases,
    revenue: resolvedRevenue,
    ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
    cpa: resolvedPurchases > 0 ? spend / resolvedPurchases : 0,
    cpm: impressions > 0 ? (spend / impressions) * 1000 : 0,
    frequency: reach > 0 ? impressions / reach : 0,
    clickToCheckout: clicks > 0 ? (checkoutViews / clicks) * 100 : 0,
    roas: spend > 0 ? resolvedRevenue / spend : 0,
  };
}

function calculateScore(metrics: MarketingMetrics, funnel: FunnelMonthlyRow, companyAvgCpa: number) {
  const roasScore = Math.min(metrics.roas / SCORE_ROAS_TARGET, 1) * 30;
  const cpaReference = companyAvgCpa > 0 ? companyAvgCpa : metrics.cpa;
  const cpaScore =
    metrics.cpa > 0 && cpaReference > 0
      ? Math.max(0, Math.min(cpaReference / metrics.cpa, 1)) * 25
      : metrics.spend > 0
      ? 0
      : 8;
  const ctrScore = Math.min(metrics.ctr / SCORE_CTR_BENCHMARK, 1) * 20;
  const checkoutScore = Math.min(metrics.clickToCheckout / SCORE_CHECKOUT_BENCHMARK, 1) * 15;
  const profitScore = funnel.profit > 0 ? 10 : funnel.profit === 0 ? 4 : 0;

  return Math.max(0, Math.min(100, roasScore + cpaScore + ctrScore + checkoutScore + profitScore));
}

function buildScoreReason(metrics: MarketingMetrics, score: number) {
  if (metrics.spend === 0 && metrics.impressions === 0) {
    return "Sin pauta conectada en el periodo.";
  }
  if (metrics.clicks > 0 && metrics.checkoutViews === 0) {
    return "Tiene clics, pero no llega al checkout.";
  }
  if (metrics.purchases === 0 && metrics.spend > 0) {
    return "Consume pauta sin compras confirmadas.";
  }
  if (metrics.roas >= SCORE_ROAS_TARGET && metrics.clickToCheckout >= SCORE_CHECKOUT_BENCHMARK) {
    return "Convierte bien desde marketing hasta compra.";
  }
  if (score >= 60) {
    return "Tiene señales sanas, pero aún puede mejorar.";
  }
  return "Revisa retorno, CPA y paso a checkout.";
}

function splitRanking(rows: FunnelScoreRow[]) {
  if (rows.length === 0) return { best: [], attention: [] };
  if (rows.length === 1) return { best: rows.slice(0, 1), attention: [] };

  const bestCount = rows.length >= 6 ? 3 : rows.length >= 3 ? Math.min(2, rows.length - 1) : 1;
  const bestSource = rows.filter((row) => row.funnel.profit >= 0);
  const best = (bestSource.length > 0 ? bestSource : [...rows].sort((a, b) => b.funnel.profit - a.funnel.profit))
    .slice(0, bestCount);
  const used = new Set(best.map((row) => row.funnel.funnelId));
  const attentionCount = rows.length >= 6 ? 3 : rows.length >= 3 ? Math.min(2, rows.length - best.length) : 1;
  const attention = [...rows]
    .filter((row) => !used.has(row.funnel.funnelId))
    .sort((a, b) => a.funnel.profit - b.funnel.profit || a.score - b.score)
    .slice(0, attentionCount);

  return { best, attention };
}
